import { useEffect, useRef, useState } from "react";
import { X, Loader2, Sparkles } from "lucide-react";
import { api, errText, media } from "../../lib/api";
import { ClaudeModelSelect } from "./ClaudeModelSelect";
import { eurOf, useEurRate } from "./ui";
import ImageCropModal from "../../components/ImageCropModal";

const LOCALES = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "es", label: "ES" },
  { code: "ar", label: "AR" },
];

const AVAILABILITY = [
  { value: "in_stock", label: "In stock" },
  { value: "made_to_order", label: "Made to order" },
  { value: "seasonal", label: "Seasonal" },
];

const Chips = ({ value, onChange, placeholder, testid }) => {
  const [draft, setDraft] = useState("");
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">
        {value.map((v) => (
          <button key={v} className="text-[11px] border border-brass px-2 py-0.5 text-muted hover:text-ink"
                  data-testid={`${testid}-chip-${v}`} onClick={() => onChange(value.filter((x) => x !== v))}>
            {v} ×
          </button>
        ))}
      </div>
      <input className="field py-1 text-xs" placeholder={placeholder} data-testid={testid} value={draft}
             onChange={(e) => setDraft(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === "Enter" && draft.trim()) {
                 e.preventDefault();
                 onChange([...new Set([...value, draft.trim()])]);
                 setDraft("");
               }
             }} />
    </div>
  );
};


// Cost (what the florist charges us) / Selling (what the buyer pays) / Margin, with partner assignment.
const PricingStrip = ({ form, set, partners }) => {
  const cost = Number(form.cost_price_mad ?? form.base_price_mad) || 0;
  const sell = Number(form.base_price_eur) || 0;
  const fx = useEurRate();
  const margin = sell - cost * (fx?.eur || 0);
  const bad = sell > 0 && margin <= 0;
  return (
    <div className={`rounded-sm border p-3 space-y-2 ${bad ? "border-danger/60 bg-danger/5" : "border-brass/40 bg-brass/5"}`} data-testid="admin-editor-pricing">
      {form.needs_selling_price ? (
        <p className="text-[11px] text-warning" data-testid="admin-editor-needs-price">Selling price not set yet — it currently equals the florist's cost price (0 margin).</p>
      ) : null}
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="smallcaps block">Partner price · MAD</span>
          <input type="number" min="0" step="0.01" className="field tabular" data-testid="admin-editor-cost-price" placeholder="MAD" value={form.cost_price_mad ?? ""}
                 onChange={(e) => set({ cost_price_mad: e.target.value, size_variants: (form.size_variants || []).map((v, i) => v.is_default || (i === 0 && !form.size_variants.some(x => x.is_default)) ? { ...v, cost_price_mad: e.target.value } : v) })} />
          <span className="text-[10px] text-muted">
            What the florist charges us, in dirhams{" "}
            <span className="text-brass tabular" data-testid="admin-editor-cost-eur">{eurOf(fx, cost)}</span>
          </span>
        </label>
        <label className="block">
          <span className="smallcaps block">Selling price · EUR</span>
          <input type="number" min="0.01" step="0.01" className="field tabular" data-testid="admin-editor-price" placeholder="EUR" value={form.base_price_eur ?? ""}
                 onChange={(e) => set({ base_price_eur: e.target.value, size_variants: (form.size_variants || []).map((v, i) => v.is_default || (i === 0 && !form.size_variants.some(x => x.is_default)) ? { ...v, price_eur: e.target.value } : v) })} />
          <span className="text-[10px] text-muted">Customer price in euros</span>
        </label>
        <div>
          <span className="smallcaps block">Margin</span>
          <p className={`font-display text-2xl tabular ${bad ? "text-danger" : "text-success"}`} data-testid="admin-editor-margin">
            {sell && fx ? `${margin > 0 ? "+" : ""}€${margin.toFixed(2)}` : "—"}
          </p>
          <span className={`text-[10px] ${bad ? "text-danger" : "text-muted"}`}>{bad ? "Selling ≤ cost — no margin" : sell ? `${Math.round((margin / sell) * 100)}% of selling price` : ""}</span>
        </div>
      </div>
      <p className="text-xs text-muted" data-testid="admin-editor-enabled-partners">Fleurinelle catalogue · {form.enabled_partner_count || 0} partners activated</p>
    </div>
  );
};

// Full admin bouquet editor. Opens over any catalogue row, any status.
// Edits per-locale title/description/care via language tabs, plus price,
// variants, occasions, photos and SEO. Saves via PUT /admin/products/{id}.
export default function AdminProductEditor({ productId, occasions = [], productTypes = [], partners = [], onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiModel, setAiModel] = useState("");
  const nextVariantKey = useRef(0);
  const [msg, setMsg] = useState("");
  const fx = useEurRate();

  useEffect(() => {
    let live = true;
    api.get(`/admin/products/${productId}`)
      .then((r) => { if (live) setForm({ ...r.data, size_variants: (r.data.size_variants || []).map(v => ({ ...v, uiKey: `variant-${nextVariantKey.current++}` })) }); })
      .catch((e) => setMsg(errText(e)));
    return () => { live = false; };
  }, [productId]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLoc = (field, val) => setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: val } }));

  // Each picked file is framed in the cropper first, then uploaded on its own.
  const uploadOne = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const { data } = await api.post(`/admin/products/${productId}/upload-image`, fd);
      setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
    } catch (e) { setMsg(errText(e)); }
    finally {
      setUploading(false);
      setQueue((q) => q.slice(1));
    }
  };

  const save = async (publish) => {
    setBusy(true); setMsg("");
    const body = {
      title: form.title, description: form.description, care_instructions: form.care_instructions,
      product_type: form.product_type,
      base_price_eur: Number(form.base_price_eur) || 0,
      cost_price_mad: form.cost_price_mad === "" || form.cost_price_mad == null ? null : Number(form.cost_price_mad),
      compare_at_price_eur: form.compare_at_price_eur ? Number(form.compare_at_price_eur) : null,
      flower_composition: form.flower_composition || [],
      dominant_colors: form.dominant_colors || [],
      occasions: form.occasions || [],
      images: form.images || [],
      size_variants: (form.size_variants || []).map((v) => ({
        name: v.name || "Standard", stem_count: Number(v.stem_count) || 1, price_eur: Number(v.price_eur) || 0,
        cost_price_mad: v.cost_price_mad === "" || v.cost_price_mad == null ? null : Number(v.cost_price_mad),
        is_default: !!v.is_default,
      })),
      prep_time_minutes: Number(form.prep_time_minutes) || 60,
      availability: form.availability,
      meta_title: form.meta_title || {}, meta_description: form.meta_description || {},
      gtin: form.gtin || "", mpn: form.mpn || "", google_category: form.google_category || "",
      publish,
    };
    try {
      const { data } = await api.put(`/admin/products/${productId}`, body);
      onSaved?.(publish === true ? "Bouquet updated & published." : publish === false ? "Saved as draft." : "Bouquet updated.", data.status);
      onClose?.();
    } catch (e) { setMsg(errText(e)); }
    finally { setBusy(false); }
  };

  const aiRewrite = async () => {
    setAiBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/admin/products/${productId}/ai-rewrite`, { model: aiModel || null });
      setForm((f) => ({
        ...f,
        description: { ...f.description, ...data.description },
        care_instructions: { ...(f.care_instructions || {}), ...data.care_instructions },
        meta_title: { ...(f.meta_title || {}), ...data.meta_title },
        meta_description: { ...(f.meta_description || {}), ...data.meta_description },
      }));
      setMsg(data.used_photo
        ? "AI rewrote the description, care note & SEO in 4 languages from the photo. Review, then save."
        : "AI rewrote the copy in 4 languages (no photo found). Review, then save.");
    } catch (e) { setMsg(errText(e)); }
    finally { setAiBusy(false); }
  };

  const occOn = (o) => (form?.occasions || []).includes(o.id) || (form?.occasions || []).includes(o.key);
  const toggleOcc = (o) => {
    const cur = form.occasions || [];
    const on = occOn(o);
    const next = on ? cur.filter((x) => x !== o.id && x !== o.key) : [...cur, o.id];
    set({ occasions: next });
  };

  return (
    <div className="fixed inset-0 bg-ink/60 z-50 flex justify-end" data-testid="admin-product-editor"
         onClick={onClose}>
      <div className="bg-plaster w-full max-w-2xl h-full overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-2xl text-ink">Edit bouquet</h3>
            {form ? <p className="text-xs text-muted">{form.shop_name} · {form.city} · <span className="capitalize">{(form.status || "").replace(/_/g, " ")}</span></p> : null}
          </div>
          <button data-testid="admin-editor-close" onClick={onClose}><X size={18} /></button>
        </div>

        {!form ? (
          <div className="flex items-center gap-2 text-muted text-sm py-10 justify-center">
            <Loader2 className="animate-spin" size={18} /> Loading…
          </div>
        ) : (
          <>
            {form.moderation_notes ? (
              <p className="text-xs text-warning border border-warning/40 bg-warning/10 p-2 rounded-sm">
                Note: {form.moderation_notes}
              </p>
            ) : null}

            {/* Language tabs for localized fields */}
            <div className="flex gap-1" data-testid="admin-editor-lang-tabs">
              {LOCALES.map((l) => (
                <button key={l.code} data-testid={`admin-editor-lang-${l.code}`}
                        onClick={() => setLang(l.code)}
                        className={`text-[11px] px-3 py-1 border ${lang === l.code ? "bg-ink text-plaster border-ink" : "border-brass text-muted"}`}>
                  {l.label}
                </button>
              ))}
              <span className="text-[11px] text-muted self-center ms-2">Editing {lang.toUpperCase()} content</span>
            </div>

            <div className="flex items-center gap-3 rounded-sm border border-brass/40 bg-brass/5 p-2.5">
              <button className="btn-primary text-xs px-3 inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="admin-editor-ai-rewrite" disabled={aiBusy} onClick={aiRewrite}>
                {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {aiBusy ? "Writing…" : "AI rewrite from photo"}
              </button>
              <p className="text-[11px] text-muted flex-1">
                Claude writes the description, care note & SEO in all 4 languages. Review before saving.
              </p>
              <ClaudeModelSelect value={aiModel} onChange={setAiModel} testid="product-claude-model" disabled={aiBusy} />
            </div>

            <input className="field" placeholder="Bouquet name" data-testid="admin-editor-title"
                   dir={lang === "ar" ? "rtl" : "ltr"}
                   value={form.title[lang] || ""} onChange={(e) => setLoc("title", e.target.value)} />
            <textarea className="field min-h-[96px]" placeholder="Description" data-testid="admin-editor-description"
                      dir={lang === "ar" ? "rtl" : "ltr"}
                      value={form.description[lang] || ""} onChange={(e) => setLoc("description", e.target.value)} />
            <textarea className="field min-h-[64px]" placeholder="Care instructions" data-testid="admin-editor-care"
                      dir={lang === "ar" ? "rtl" : "ltr"}
                      value={(form.care_instructions || {})[lang] || ""} onChange={(e) => setLoc("care_instructions", e.target.value)} />

            <PricingStrip form={form} set={set} partners={partners} />

            <div className="grid sm:grid-cols-2 gap-2">
              <select className="field" data-testid="admin-editor-type" value={form.product_type}
                      onChange={(e) => set({ product_type: e.target.value })}>
                {(productTypes.length ? productTypes : [{ key: "hand-tied", label: "Hand tied" }]).map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <input type="number" min="0.01" step="0.01" className="field" placeholder="Compare-at EUR (optional)" data-testid="admin-editor-compare-price"
                     value={form.compare_at_price_eur || ""} onChange={(e) => set({ compare_at_price_eur: e.target.value })} />
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
              <select className="field" data-testid="admin-editor-availability" value={form.availability}
                      onChange={(e) => set({ availability: e.target.value })}>
                {AVAILABILITY.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              <input className="field" placeholder="Prep time (min)" data-testid="admin-editor-prep"
                     value={form.prep_time_minutes} onChange={(e) => set({ prep_time_minutes: e.target.value.replace(/\D/g, "") })} />
            </div>

            <div className="space-y-2">
              <p className="smallcaps">Sizes · cost / selling per size</p>
              {(form.size_variants || []).map((v, i) => (
                <div key={v.uiKey} className="flex flex-wrap gap-2 items-center" data-testid={`admin-variant-row-${i}`}>
                  <input className="field py-1 text-xs w-28" placeholder="Name" value={v.name || ""} data-testid={`admin-variant-name-${i}`}
                         onChange={(e) => set({ size_variants: form.size_variants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                  <input className="field py-1 text-xs w-20" placeholder="Stems" value={v.stem_count ?? ""} data-testid={`admin-variant-stems-${i}`}
                         onChange={(e) => set({ size_variants: form.size_variants.map((x, j) => (j === i ? { ...x, stem_count: e.target.value.replace(/\D/g, "") } : x)) })} />
                  <input type="number" min="0" step="0.01" className="field py-1 text-xs w-24" placeholder="Cost MAD" title="Cost price (partner)" data-testid={`admin-variant-cost-${i}`} value={v.cost_price_mad ?? ""}
                         onChange={(e) => set({ size_variants: form.size_variants.map((x, j) => (j === i ? { ...x, cost_price_mad: e.target.value } : x)) })} />
                  <input type="number" min="0.01" step="0.01" className="field py-1 text-xs w-24" placeholder="Sell EUR" title="Selling price EUR" data-testid={`admin-variant-price-${i}`} value={v.price_eur ?? ""}
                         onChange={(e) => set({ size_variants: form.size_variants.map((x, j) => (j === i ? { ...x, price_eur: e.target.value } : x)) })} />
                  <span className="text-[10px] tabular text-brass w-20" data-testid={`admin-variant-cost-eur-${i}`}>
                    {eurOf(fx, v.cost_price_mad ?? v.price_mad)}
                  </span>
                  <label className="text-[11px] text-muted flex items-center gap-1">
                    <input type="radio" name="admin-default-variant" checked={!!v.is_default} data-testid={`admin-variant-default-${i}`}
                           onChange={() => set({ size_variants: form.size_variants.map((x, j) => ({ ...x, is_default: j === i })) })} />
                    default
                  </label>
                  <button className="text-danger text-xs" data-testid={`admin-variant-remove-${i}`}
                          onClick={() => set({ size_variants: form.size_variants.filter((_, j) => j !== i) })}>×</button>
                </div>
              ))}
              <button className="btn-ghost text-[11px] py-1 px-2" data-testid="admin-variant-add"
                      onClick={() => set({ size_variants: [...(form.size_variants || []), { uiKey: `variant-${nextVariantKey.current++}`, name: "", stem_count: 15, price_eur: form.base_price_eur, cost_price_mad: form.cost_price_mad ?? 0, is_default: !(form.size_variants || []).length }] })}>
                Add a size
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <p className="smallcaps">Flowers used</p>
                <Chips testid="admin-editor-flowers" value={form.flower_composition || []} placeholder="rose, peony… then Enter"
                       onChange={(v) => set({ flower_composition: v })} />
              </div>
              <div>
                <p className="smallcaps">Dominant colours</p>
                <Chips testid="admin-editor-colors" value={form.dominant_colors || []} placeholder="cream, coral… then Enter"
                       onChange={(v) => set({ dominant_colors: v })} />
              </div>
            </div>

            <div>
              <p className="smallcaps">Occasions</p>
              <div className="flex flex-wrap gap-1 mt-1" data-testid="admin-editor-occasions">
                {occasions.map((o) => (
                  <button key={o.id} data-testid={`admin-editor-occasion-${o.id}`}
                          className={`text-[11px] border px-2 py-0.5 ${occOn(o) ? "bg-ink text-plaster border-ink" : "border-brass text-muted"}`}
                          onClick={() => toggleOcc(o)}>
                    {o.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <p className="smallcaps">Photos ({(form.images || []).length})</p>
              <div className="flex flex-wrap gap-2">
                {(form.images || []).map((src, i) => (
                  <div key={`${src}-${i}`} className="relative" data-testid={`admin-editor-image-${i}`}>
                    <img src={media(src)} alt="" className="w-20 h-20 object-cover" />
                    <div className="flex gap-1 mt-1">
                      {i > 0 ? <button className="text-[10px] text-muted" data-testid={`admin-image-up-${i}`}
                                       onClick={() => set({ images: form.images.map((x, j) => (j === i - 1 ? form.images[i] : j === i ? form.images[i - 1] : x)) })}>◀</button> : null}
                      {i < form.images.length - 1 ? <button className="text-[10px] text-muted" data-testid={`admin-image-down-${i}`}
                                       onClick={() => set({ images: form.images.map((x, j) => (j === i + 1 ? form.images[i] : j === i ? form.images[i + 1] : x)) })}>▶</button> : null}
                      <button className="text-[10px] text-danger" data-testid={`admin-image-remove-${i}`}
                              onClick={() => set({ images: form.images.filter((_, j) => j !== i) })}>del</button>
                      {i === 0 ? <span className="text-[10px] text-brass">cover</span> : null}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*" multiple data-testid="admin-editor-image-upload"
                       onChange={(e) => { setQueue(Array.from(e.target.files || [])); e.target.value = ""; }} />
                {uploading ? <Loader2 className="animate-spin text-brass" size={14} /> : null}
              </div>
            </div>

            <details className="card p-3">
              <summary className="smallcaps cursor-pointer">SEO & Google Merchant</summary>
              <div className="space-y-2 mt-3">
                <p className="text-[11px] text-muted">SEO title & description are per-language — editing the <b>{lang.toUpperCase()}</b> tab above.</p>
                <input className="field" placeholder="SEO title (70 chars)" data-testid="admin-editor-meta-title"
                       dir={lang === "ar" ? "rtl" : "ltr"}
                       value={(form.meta_title || {})[lang] || ""} onChange={(e) => setLoc("meta_title", e.target.value)} />
                <textarea className="field min-h-[60px]" placeholder="SEO description (180 chars)" data-testid="admin-editor-meta-description"
                          dir={lang === "ar" ? "rtl" : "ltr"}
                          value={(form.meta_description || {})[lang] || ""} onChange={(e) => setLoc("meta_description", e.target.value)} />
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className="field" placeholder="GTIN (optional)" data-testid="admin-editor-gtin"
                         value={form.gtin || ""} onChange={(e) => set({ gtin: e.target.value })} />
                  <input className="field" placeholder="Internal ref / MPN" data-testid="admin-editor-mpn"
                         value={form.mpn || ""} onChange={(e) => set({ mpn: e.target.value })} />
                </div>
                <input className="field" placeholder="Google category" data-testid="admin-editor-google-category"
                       value={form.google_category || ""} onChange={(e) => set({ google_category: e.target.value })} />
              </div>
            </details>

            {msg ? <p className="text-sm text-danger" data-testid="admin-editor-message">{msg}</p> : null}

            <div className="flex flex-wrap gap-2 pt-2 sticky bottom-0 bg-plaster py-3 -mx-6 px-6 border-t border-brass-light">
              <button className="btn-primary text-xs px-4" data-testid="admin-editor-save" disabled={busy}
                      onClick={() => save(null)}>Save changes</button>
              <button className="btn-ghost text-xs px-4 border border-ink" data-testid="admin-editor-save-publish" disabled={busy}
                      onClick={() => save(true)}>Save & publish</button>
              {form.status !== "draft" ? (
                <button className="btn-ghost text-xs px-4" data-testid="admin-editor-save-draft" disabled={busy}
                        onClick={() => save(false)}>Save as draft</button>
              ) : null}
              {busy ? <Loader2 className="animate-spin text-brass self-center" size={16} /> : null}
            </div>
          </>
        )}
      </div>
      {queue.length ? (
        <ImageCropModal file={queue[0]} aspect={4 / 5}
                        title={`Frame photo ${form.images?.length ? form.images.length + 1 : 1}`}
                        onCancel={() => setQueue((q) => q.slice(1))} onDone={uploadOne} />
      ) : null}
    </div>
  );
}
